import { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { useFetchApplications } from '../../services/application.service';
import { Application } from '../../models/application.model';
import { Project } from '../../models/project.model';
import {
  useTranslation,
  DropdownButton,
  Icon,
  DebouncedSearchInput,
  InfiniteScrollV2,
  DropdownItem,
  Empty,
} from '@testsigma/atoms';
import noResultFound from '@images/no-result-found.svg';
import { PROJECT_TYPE } from './constants';
import classNames from 'classnames';
import { UserPreferenceContext } from '@contexts/user-preference-context';
import { ListItemSkeleton } from '@modules/test-development/components/list-item-skeleton';
import { ApplicationType } from '@testsigma/organism';

const { VirtualizedInfiniteLoader } = InfiniteScrollV2;

interface ApplicationAutoCompleteProps {
  project: Project;
  selectedItem: Application | null;
  updateSelected: (data: Application) => void;
  setIsFetchingApplications: (isFetching: boolean) => void;
  dropdownClassName?: string;
  query?: string;
  isFromSfGlobalPage?: boolean
}

const ApplicationAutoComplete = ({
  project,
  selectedItem,
  updateSelected,
  setIsFetchingApplications,
  dropdownClassName = '',
  query = '',
  isFromSfGlobalPage = false,
}: ApplicationAutoCompleteProps) => {
  const userPreferenceContext = useContext(UserPreferenceContext);
  const projectPreference =
    userPreferenceContext?.userPreference.preference.projectPreference;
  const { t } = useTranslation();
  const [isOpen, updateIsOpen] = useState(false);
  const [applications, setApplications] = useState<Application[]>([]);
  const [value, setValue] = useState('');

  const filterQuery = useMemo(
    () =>
      `projectId:${project?.id}${value ? `,name:*${value}*` : ''}${
        query ? `,${query}` : ''
      }${isFromSfGlobalPage ? `,applicationType:${ApplicationType.Salesforce}` : ''}`,
    [project?.id, value, query],
  );

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useFetchApplications({
      filterQuery,
      queryKey: [project?.id, value],
      enabled: !!project?.id,
      keepPreviousData: !value,
    });

  const rowRenderer = useCallback(
    (index: number) => {
      const item = applications[index];
      const isSelected = item?.id === selectedItem?.id;

      return (
        <DropdownItem
          key={item?.id}
          onClick={() => {
            updateSelected(item);
            updateIsOpen(false);
          }}
          className='flex items-center text-unique-600 hover:!bg-secondary-50 hover:!text-unique-600'
          isSelected={isSelected}
        >
          <Icon
            className='text-[18px] pr-md text-unique-800'
            name={PROJECT_TYPE.icon[item?.applicationType]}
          />
          <div className='flex w-full items-center justify-between'>
            <div className='truncate w-[180px]' title={item?.name}>
              {item?.name}
            </div>
            {isSelected && (
              <Icon
                name='check-circle'
                className='text-primary-500 text-base'
              />
            )}
          </div>
        </DropdownItem>
      );
    },
    [applications, selectedItem, updateSelected, updateIsOpen],
  );

  useEffect(() => {
    const { projectId, applicationId, selectedApplication } =
      projectPreference || {};
    let mergedContent = data?.pages?.flatMap((page) => page.content) || [];

    if (!!value) {
      return setApplications(mergedContent);
    }

    if (isFromSfGlobalPage || projectId !== project?.id) {
      setApplications(mergedContent);
      updateSelected(mergedContent[0]);
      return;
    }

    let preferredApplication = mergedContent.find(
      (app: Application) => app?.id === applicationId,
    );

    if (applicationId === selectedApplication?.id && !preferredApplication) {
      mergedContent = [selectedApplication, ...mergedContent];
      preferredApplication = selectedApplication;
    }

    setApplications(mergedContent);

    if (isFromSfGlobalPage || applicationId !== selectedApplication?.id) {
      updateSelected(
        preferredApplication || mergedContent[0] || 'Default Application',
      );
    }
  }, [data, value, project?.id]);

  useEffect(() => {
    setIsFetchingApplications(isFetching);
  }, [isFetching]);

  return (
    <DropdownButton
      name={
        <div className='flex items-center'>
          <Icon
            className='text-[18px] pr-md text-unique-800'
            name={PROJECT_TYPE.icon[selectedItem?.applicationType]}
          />
          {selectedItem?.name}
        </div>
      }
      label={t('project_switcher.application')}
      variant='select-fill'
      placement='bottom'
      dropdownClassName={`!min-h-[240px] !max-h-[240px] !overflow-auto ${dropdownClassName}`}
      wrapperClassName={classNames('mb-[18px]')}
      labelClassName='mb-1'
      onClick={() => setValue('')}
      hideOnChildClick={false}
      setIsOpen={updateIsOpen}
      isOpen={isOpen}
    >
      <div className='h-full'>
        <DebouncedSearchInput
          value={value}
          onChange={setValue}
          placeholder={t('common.search')}
          wrapperClassName='outline-none w-full bg-white p-md sticky top-0 z-10'
        />

        {isFetching && !isFetchingNextPage ? (
          <ListItemSkeleton />
        ) : applications.length ? (
          <VirtualizedInfiniteLoader
            rowHeight={35}
            listHeight={175}
            rowRenderer={rowRenderer}
            rowCount={applications.length}
            items={applications}
            loadMore={fetchNextPage}
            hasMore={hasNextPage}
            isLoading={isFetching}
            LoadingComponent={<ListItemSkeleton />}
          />
        ) : (
          <Empty
            description={<span>{t('no.results.found')}</span>}
            image={
              <img
                src={noResultFound}
                alt='empty-state-logo'
                className='mb-3'
              />
            }
            imageSource={''}
          />
        )}
      </div>
    </DropdownButton>
  );
};

export default ApplicationAutoComplete;
